import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { Card, EmptyState, ErrorBanner, Spinner, StatusBadge } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { CellaTag, useTagDi } from '../components/Tag'
import type { BusinessService, ServiceEntry } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

const CRITICALITY: { value: string; label: () => string }[] = [
  { value: 'critical', label: () => t('Critico') },
  { value: 'high', label: () => t('Alta') },
  { value: 'medium', label: () => t('Media') },
  { value: 'low', label: () => t('Bassa') },
]

/** Etichette per valore. Getter e non valori: si traducono quando si leggono,
 *  non all'import del modulo. */
export const CRITICALITY_LABEL: Record<string, string> = Object.defineProperties(
  {} as Record<string, string>,
  Object.fromEntries(CRITICALITY.map((c) => [c.value, { get: c.label, enumerable: true }])),
)

// Le descrizioni automatiche dei servizi («Creato dalla composizione di X»,
// «Creato dalla mappa di correlazione di X (finestra Y)») sono generate con t()
// alla creazione e SALVATE come stringa: restano quindi nella lingua di allora.
// Qui si riconosce il modello e si ri-traduce al render, così seguono la lingua
// scelta. Una descrizione scritta a mano non combacia e si mostra invariata.
const RE_CORRELAZIONE = /^Creato dalla mappa di correlazione di (.+) \(finestra (.+)\)$/
const RE_COMPOSIZIONE = /^Creato dalla composizione di (.+)$/
export function descrizioneServizio(d: string | null | undefined): string {
  if (!d) return ''
  const corr = RE_CORRELAZIONE.exec(d)
  if (corr) {
    return t('Creato dalla mappa di correlazione di {seed} (finestra {finestra})', {
      seed: corr[1],
      finestra: corr[2],
    })
  }
  const comp = RE_COMPOSIZIONE.exec(d)
  if (comp) return t('Creato dalla composizione di {seed}', { seed: comp[1] })
  return d
}

/**
 * I servizi di business: l'elenco di ciò di cui parlano le persone che non
 * guardano la CMDB.
 *
 * Un servizio si definisce indicando **da dove ci si entra**; il resto lo
 * ricava il traffico osservato. È l'unica cosa che nessuna discovery può
 * dedurre: dai flussi si vede che due macchine parlano, non che una di esse è
 * il punto in cui i clienti entrano nella fatturazione.
 */
export default function Services() {
  const services = useAsync(() => api.services(), [])
  const [creating, setCreating] = useState(false)
  const tag = useTagDi('business_service')

  const colonne: Column<BusinessService>[] = [
    {
      key: 'name',
      header: t('Servizio'),
      value: (s) => `${s.name}${s.description ? ` ${descrizioneServizio(s.description)}` : ''}`,
      render: (s) => (
        <>
          <Link to={`/services/${s.id}`}>{s.name}</Link>
          {s.description && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {descrizioneServizio(s.description)}
            </div>
          )}
        </>
      ),
    },
    // Tags come from the shared catalogue: filtered on their «key:value» text
    // and edited in place.
    ...(tag.vede
      ? [{
          key: 'tag',
          header: t('Tag'),
          sort: false as const,
          filter: 'text' as const,
          value: (s: BusinessService) => tag.testo(s.id),
          render: (s: BusinessService) => (
            <CellaTag tipo="business_service" id={s.id} tags={tag.di(s.id)} scrive={tag.scrive} onChanged={() => void tag.reload()} />
          ),
        } as Column<BusinessService>]
      : []),
    {
      key: 'criticality',
      header: t('Criticità'),
      value: (s) => CRITICALITY_LABEL[s.criticality] ?? s.criticality,
      render: (s) => <StatusBadge status={s.criticality} />,
    },
    { key: 'owner', header: t('Responsabile') },
    {
      key: 'entries',
      header: t('Punti di ingresso'),
      value: (s) =>
        s.entries.length === 0
          ? t('nessuno: la mappa sarà vuota')
          : s.entries.map((e) => `${e.ci_name ?? e.address}${e.port ? `:${e.port}` : ''}`).join(', '),
      render: (s) =>
        s.entries.length === 0 ? (
          // Without entries the map is empty, and an empty map reads as «this
          // service has no dependencies».
          <span style={{ color: 'var(--warn)' }}>{t('nessuno: la mappa sarà vuota')}</span>
        ) : (
          s.entries.map((e) => (
            <span className="chip" key={e.id}>
              {e.ci_name ?? e.address}
              {e.port ? `:${e.port}` : ''}
            </span>
          ))
        ),
    },
    {
      key: 'max_depth',
      header: t('Profondità'),
      type: 'number',
      align: 'left',
      render: (s) => (s.max_depth === 1 ? t('1 salto') : t('{n} salti', { n: s.max_depth })),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Business Service')}</h1>
          <p className="page__subtitle">
            {t('Servizi erogati dall’infrastruttura, identificati e ricostruiti tramite l’analisi dei flussi di rete osservati. La composizione dei servizi include componenti applicativi, dipendenze e infrastruttura sottostante, con evidenza delle relazioni tra i sistemi che contribuiscono alla loro erogazione.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <BottoniEsporta
            path={(fmt) => api.servicesExportPath(fmt)}
            nome="censo-business-service"
            disabled={!services.data?.length}
          />
          <button onClick={() => setCreating((open) => !open)}>
            {creating ? t('Annulla') : t('Nuovo servizio')}
          </button>
        </div>
      </header>

      <ErrorBanner error={services.error} />

      {creating && (
        <ServiceForm
          onSaved={() => {
            setCreating(false)
            services.reload()
          }}
        />
      )}

      <Card title={t('Servizi definiti')}>
        {services.loading && !services.data ? (
          <Spinner />
        ) : !services.data?.length ? (
          <EmptyState
            title={t('Nessun servizio definito')}
            hint={t("Definisci un servizio indicando il suo punto di ingresso — il VIP del bilanciatore, il front-end, l'indirizzo pubblicato — e la sua mappa si costruirà dal traffico osservato.")}
          />
        ) : (
          <DataTable
            rows={services.data}
            columns={colonne}
            rowKey={(s) => s.id}
            conteggio={{ nome: t('servizi') }}
          />
        )}
      </Card>
    </>
  )
}

/** Modulo di definizione di un servizio e dei suoi ingressi. */
export function ServiceForm({
  onSaved,
  initial,
  serviceId,
}: {
  onSaved: () => void
  initial?: {
    name: string
    description: string | null
    criticality: string
    owner: string | null
    max_depth: number
    entries: ServiceEntry[]
  }
  serviceId?: string
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [criticality, setCriticality] = useState(initial?.criticality ?? 'medium')
  const [owner, setOwner] = useState(initial?.owner ?? '')
  const [maxDepth, setMaxDepth] = useState(initial?.max_depth ?? 3)
  const [entries, setEntries] = useState<ServiceEntry[]>(
    initial?.entries?.length
      ? initial.entries
      : [{ ci_id: null, address: '', port: null, protocol: null, label: '' }],
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const cis = useAsync(() => api.cis({ limit: 500 }), [])

  function patch(index: number, changes: Partial<ServiceEntry>) {
    setEntries((current) => current.map((e, i) => (i === index ? { ...e, ...changes } : e)))
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        criticality,
        owner: owner.trim() || null,
        max_depth: maxDepth,
        entries: entries
          // Le righe lasciate vuote si scartano invece di far fallire il
          // salvataggio: se ne aggiunge una per errore continuamente.
          .filter((e) => e.ci_id || (e.address ?? '').trim())
          .map((e) => ({
            ci_id: e.ci_id || null,
            address: (e.address ?? '').trim() || null,
            port: e.port || null,
            protocol: null,
            label: (e.label ?? '').trim() || null,
          })),
      }
      if (serviceId) await api.updateService(serviceId, payload)
      else await api.createService(payload)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={serviceId ? t('Modifica servizio') : t('Nuovo servizio')}>
      <ErrorBanner error={error} />

      <div className="row">
        <div className="field">
          <label htmlFor="svc-name">{t('Nome')}</label>
          <input
            id="svc-name"
            type="text"
            value={name}
            placeholder={t('es. Portale clienti')}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="svc-crit">{t('Criticità')}</label>
          <select
            id="svc-crit"
            value={criticality}
            onChange={(e) => setCriticality(e.target.value)}
          >
            {CRITICALITY.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label()}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="svc-owner">{t('Responsabile')}</label>
          <input
            id="svc-owner"
            type="text"
            value={owner}
            placeholder={t('es. Team Digital')}
            onChange={(e) => setOwner(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="svc-desc">{t('Descrizione')}</label>
        <input
          id="svc-desc"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="field">
        <label>{t('Punti di ingresso')}</label>
        <p className="field__hint" style={{ marginBottom: 8 }}>
          {t("Da dove si entra nel servizio: il VIP del bilanciatore, il front-end, l'indirizzo pubblicato. È l'unica cosa da dichiarare a mano — dai flussi si vede che due macchine parlano, non quale delle due è l'ingresso. Il resto della mappa si ricava seguendo il traffico da qui.")}
        </p>

        {entries.map((entry, index) => (
          <div className="row" key={index} style={{ marginBottom: 8, alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`entry-ci-${index}`}>{t('Configuration Item')}</label>
              <select
                id={`entry-ci-${index}`}
                value={entry.ci_id ?? ''}
                onChange={(e) => patch(index, { ci_id: e.target.value || null })}
              >
                <option value="">{t('— indica un indirizzo —')}</option>
                {(cis.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name ?? a.primary_key}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`entry-addr-${index}`}>{t('oppure indirizzo')}</label>
              <input
                id={`entry-addr-${index}`}
                type="text"
                value={entry.address ?? ''}
                placeholder={t('es. {esempio}', { esempio: '192.168.10.10' })}
                disabled={!!entry.ci_id}
                onChange={(e) => patch(index, { address: e.target.value })}
              />
            </div>
            <div className="field" style={{ marginBottom: 0, maxWidth: 110 }}>
              <label htmlFor={`entry-port-${index}`}>{t('Porta')}</label>
              <input
                id={`entry-port-${index}`}
                type="number"
                value={entry.port ?? ''}
                placeholder="443"
                onChange={(e) =>
                  patch(index, { port: e.target.value ? Number(e.target.value) : null })
                }
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`entry-label-${index}`}>{t('Etichetta')}</label>
              <input
                id={`entry-label-${index}`}
                type="text"
                value={entry.label ?? ''}
                placeholder={t('es. VIP portale')}
                onChange={(e) => patch(index, { label: e.target.value })}
              />
            </div>
            <button
              className="secondary"
              style={{ flex: '0 0 auto', minWidth: 0 }}
              onClick={() => setEntries((c) => c.filter((_, i) => i !== index))}
              disabled={entries.length === 1}
            >
              {t('Togli')}
            </button>
          </div>
        ))}

        <button
          className="secondary"
          onClick={() =>
            setEntries((c) => [
              ...c,
              { ci_id: null, address: '', port: null, protocol: null, label: '' },
            ])
          }
        >
          {t('Aggiungi ingresso')}
        </button>
      </div>

      <div className="field" style={{ maxWidth: 260 }}>
        <label htmlFor="svc-depth">{t('Profondità della mappa')}</label>
        <select
          id="svc-depth"
          value={maxDepth}
          onChange={(e) => setMaxDepth(Number(e.target.value))}
        >
          {[1, 2, 3, 4, 5].map((d) => (
            <option key={d} value={d}>
              {d === 1 ? t('1 salto') : t('{n} salti', { n: d })}
            </option>
          ))}
        </select>
        <p className="field__hint">
          {t('Due bastano per «front-end → applicativo → database». Su una rete piatta alzarla include mezza infrastruttura.')}
        </p>
      </div>

      <button onClick={submit} disabled={busy || !name.trim()}>
        {busy ? t('Salvataggio…') : serviceId ? t('Salva modifiche') : t('Crea servizio')}
      </button>
    </Card>
  )
}
